(() => {
  const CONFIG_ID = 'MyCanteenPromoEngineConfig';
  const ENGINE_SOURCE = 'mycanteen-promo-engine';
  const PRIVATE_GIFT_FLAG = '_mc_promo_gift';
  const PRIVATE_PROMO_ID = '_mc_promo_id';
  const RECONCILE_DELAY = 80;
  const TOAST_DURATION = 4500;

  class MyCanteenPromoEngine {
    constructor(config) {
      this.config = config;
      this.rules = (config.rules || []).filter((rule) => rule.enabled);
      this.root = window.Shopify?.routes?.root || config.routesRoot || '/';
      this.busy = false;
      this.timer = null;
      this.toastTimer = null;
      this.lastCart = null;
    }

    start() {
      if (!this.config.enabled || this.rules.length === 0) return;

      document.addEventListener('cart:update', (event) => {
        if (event.detail?.data?.source === ENGINE_SOURCE) return;
        this.schedule();
      });

      document.addEventListener('discount:update', () => this.schedule());


      window.addEventListener('pageshow', () => this.schedule(0));
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden) this.schedule(0);
      });

      this.schedule(0);
    }

    schedule(delay = RECONCILE_DELAY) {
      window.clearTimeout(this.timer);
      this.timer = window.setTimeout(() => this.reconcile(), delay);
    }

    async reconcile() {
      if (this.busy) return;
      this.busy = true;

      try {
        await this.resolveMissingGiftVariants();
        let cart = await this.getCart();
        const qualifyingSubtotal = this.getQualifyingSubtotal(cart);
        let changed = false;

        for (const rule of this.rules) {
          if (!rule.giftVariantId) continue;

          const qualifies = qualifyingSubtotal >= rule.thresholdCents;
          let giftLine = this.findGiftLine(cart, rule.id);

          if (!qualifies) {
            if (giftLine) {
              cart = await this.changeLine(giftLine.key, 0);
              changed = true;
              this.showToast(this.formatMessage(rule.removedMessage, rule, qualifyingSubtotal));
            }
            continue;
          }

          if (!giftLine) {
            if (!rule.giftAvailable) {
              this.showToast(this.formatMessage(rule.unavailableMessage, rule, qualifyingSubtotal));
              continue;
            }

            try {
              await this.addGift(rule);
              cart = await this.getCart();
              giftLine = this.findGiftLine(cart, rule.id);
              changed = true;
            } catch (error) {
              console.warn('[MyCanteen Promo] Unable to add gift:', error);
              this.showToast(this.formatMessage(rule.unavailableMessage, rule, qualifyingSubtotal));
              continue;
            }
          }

          if (giftLine && giftLine.quantity !== rule.giftQuantity) {
            cart = await this.changeLine(giftLine.key, rule.giftQuantity);
            giftLine = this.findGiftLine(cart, rule.id);
            changed = true;
          }

          if (giftLine && !this.isGiftFree(giftLine)) {
            // Give Shopify one short reconciliation cycle to apply an automatic discount
            // before deciding the gift is not actually free. This avoids removing a gift
            // during a transient cart-update race.
            await new Promise((resolve) => window.setTimeout(resolve, 180));
            cart = await this.getCart();
            giftLine = this.findGiftLine(cart, rule.id);

            if (giftLine && !this.isGiftFree(giftLine)) {
              cart = await this.changeLine(giftLine.key, 0);
              changed = true;

              const shopperMessage = this.formatMessage(rule.unavailableMessage, rule, qualifyingSubtotal);
              const designModeMessage = `Promo setup needed: ${rule.giftLabel || rule.giftProductTitle} was not discounted to $0.00, so it was removed. Create/verify the matching automatic Buy X Get Y discount.`;
              this.showToast(window.Shopify?.designMode ? designModeMessage : shopperMessage);
              console.warn('[MyCanteen Promo] Gift removed because Shopify did not discount it to $0.00. Verify the automatic Buy X Get Y discount.', rule);
              continue;
            }
          }

          if (giftLine && changed) {
            this.showToast(this.formatMessage(rule.unlockedMessage, rule, qualifyingSubtotal));
          }
        }

        this.lastCart = cart;
        this.renderProgress(cart);

        if (changed) {
          this.dispatchCartUpdate(cart);
        }
      } catch (error) {
        console.warn('[MyCanteen Promo] Reconciliation failed:', error);
      } finally {
        this.busy = false;
      }
    }

    // Promotion thresholds never gate checkout. They only control gift eligibility/progress.

    getGiftHandleCandidates(rule) {
      const raw = String(rule.giftProductHandle || '').trim().toLowerCase();
      const candidates = [];
      const add = (value) => {
        const handle = String(value || '').trim().toLowerCase();
        if (handle && !candidates.includes(handle)) candidates.push(handle);
      };

      add(raw);

      // The Cassava Flour title/handle changed during the store build. Keep both
      // the original imported handle and the current simplified handle working.
      if (raw.startsWith('obiji-')) add(raw.replace(/^obiji-/, ''));
      add('cassava-flour-fufu');
      add('obiji-cassava-flour-fufu');

      return candidates;
    }

    async resolveMissingGiftVariants() {
      const unresolved = this.rules.filter(
        (rule) => (!rule.giftVariantId || !rule.giftAvailable) && !rule._resolutionAttempted
      );

      if (!unresolved.length) return;

      await Promise.all(
        unresolved.map(async (rule) => {
          rule._resolutionAttempted = true;
          const candidates = this.getGiftHandleCandidates(rule);

          for (const candidate of candidates) {
            try {
              const handle = encodeURIComponent(candidate);
              const response = await fetch(`${this.root}products/${handle}.js`, {
                headers: { Accept: 'application/json' },
                credentials: 'same-origin',
                cache: 'no-store',
              });
              if (!response.ok) continue;

              const product = await response.json();
              const variants = Array.isArray(product?.variants) ? product.variants : [];
              const availableVariant = variants.find((variant) => variant.available);
              if (!availableVariant?.id) continue;

              rule.giftProductHandle = product.handle || candidate;
              rule.giftVariantId = Number(availableVariant.id);
              rule.giftAvailable = true;
              rule.giftProductTitle = product.title || rule.giftProductTitle;
              return;
            } catch (error) {
              console.warn('[MyCanteen Promo] Gift product lookup failed:', candidate, error);
            }
          }

          rule.giftAvailable = false;
          console.warn('[MyCanteen Promo] No available gift variant was found for:', candidates);
        })
      );
    }

    async getCart() {
      const response = await fetch(`${this.root}cart.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });

      if (!response.ok) throw new Error(`Cart request failed (${response.status})`);
      return response.json();
    }

    async addGift(rule) {
      const response = await fetch(`${this.root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          items: [
            {
              id: Number(rule.giftVariantId),
              quantity: Number(rule.giftQuantity) || 1,
              properties: {
                [PRIVATE_GIFT_FLAG]: 'true',
                [PRIVATE_PROMO_ID]: rule.id,
              },
            },
          ],
        }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || `Gift add failed (${response.status})`);
      }
      return data;
    }

    async changeLine(key, quantity) {
      const response = await fetch(`${this.root}cart/change.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        credentials: 'same-origin',
        body: JSON.stringify({ id: key, quantity }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || `Cart change failed (${response.status})`);
      }
      return data;
    }

    findGiftLine(cart, promoId) {
      return (cart.items || []).find(
        (item) => item.properties?.[PRIVATE_GIFT_FLAG] === 'true' && item.properties?.[PRIVATE_PROMO_ID] === promoId
      );
    }

    isPromoGift(item) {
      return item.properties?.[PRIVATE_GIFT_FLAG] === 'true';
    }

    getQualifyingSubtotal(cart) {
      return (cart.items || []).reduce((sum, item) => {
        if (this.isPromoGift(item)) return sum;
        return sum + Number(item.original_line_price || 0);
      }, 0);
    }

    isGiftFree(item) {
      return Number(item.final_line_price) === 0;
    }

    dispatchCartUpdate(cart) {
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cart,
            sourceId: ENGINE_SOURCE,
            data: {
              source: ENGINE_SOURCE,
              itemCount: Number(cart.item_count || 0),
            },
          },
        })
      );
    }

    renderProgress(cart) {
      if (!this.config.showCartProgress) return;

      const qualifyingSubtotal = this.getQualifyingSubtotal(cart);
      const containers = document.querySelectorAll('[data-mc-promo-progress]');
      if (!containers.length) return;

      const enabledRules = this.rules.filter((rule) => rule.thresholdCents > 0);
      if (!enabledRules.length) return;

      const sortedRules = enabledRules.slice().sort((a, b) => a.thresholdCents - b.thresholdCents);
      const rule = sortedRules.find((candidate) => qualifyingSubtotal < candidate.thresholdCents) || sortedRules[sortedRules.length - 1];

      const qualifies = qualifyingSubtotal >= rule.thresholdCents;
      const giftLine = this.findGiftLine(cart, rule.id);
      const percent = Math.max(0, Math.min(100, (qualifyingSubtotal / Math.max(rule.thresholdCents, 1)) * 100));

      let message;
      if (qualifies && giftLine && this.isGiftFree(giftLine)) {
        message = this.formatMessage(rule.unlockedMessage, rule, qualifyingSubtotal);
      } else if (qualifies && !rule.giftVariantId) {
        message = this.formatMessage(rule.unavailableMessage, rule, qualifyingSubtotal);
      } else if (qualifies) {
        message = `You've unlocked a FREE ${this.escapeText(rule.giftLabel || rule.giftProductTitle || 'gift')}!`;
      } else {
        message = this.formatMessage(rule.progressMessage, rule, qualifyingSubtotal);
      }

      containers.forEach((container) => {
        container.hidden = false;
        container.classList.toggle('mc-promo-progress--unlocked', qualifies && !!giftLine && this.isGiftFree(giftLine));
        container.style.setProperty('--mc-promo-progress', `${percent}%`);
        const messageNode = container.querySelector('[data-mc-promo-progress-message]');
        if (messageNode) messageNode.textContent = message;
      });
    }

    formatMessage(template, rule, qualifyingSubtotal) {
      const threshold = this.money(rule.thresholdCents);
      const remaining = this.money(Math.max(0, rule.thresholdCents - qualifyingSubtotal));
      const gift = rule.giftLabel || rule.giftProductTitle || 'gift';

      return String(template || '')
        .replaceAll('[threshold]', threshold)
        .replaceAll('[remaining]', remaining)
        .replaceAll('[gift]', gift);
    }

    money(cents) {
      const currency = this.config.currency || 'USD';
      const locale = document.documentElement.lang || 'en-US';
      try {
        return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(Number(cents || 0) / 100);
      } catch (_) {
        return `$${(Number(cents || 0) / 100).toFixed(2)}`;
      }
    }

    escapeText(value) {
      return String(value || '').replace(/[<>]/g, '');
    }

    showToast(message) {
      if (!this.config.showToasts || !message) return;
      const toast = document.querySelector('[data-mc-promo-toast]');
      if (!toast) return;

      window.clearTimeout(this.toastTimer);
      toast.textContent = message;
      toast.hidden = false;
      requestAnimationFrame(() => toast.classList.add('is-visible'));

      this.toastTimer = window.setTimeout(() => {
        toast.classList.remove('is-visible');
        window.setTimeout(() => {
          if (!toast.classList.contains('is-visible')) toast.hidden = true;
        }, 220);
      }, TOAST_DURATION);
    }
  }

  function boot() {
    const configNode = document.getElementById(CONFIG_ID);
    if (!configNode || configNode.dataset.booted === 'true') return;

    try {
      const config = JSON.parse(configNode.textContent || '{}');
      configNode.dataset.booted = 'true';
      const engine = new MyCanteenPromoEngine(config);
      window.MyCanteenPromoEngine = engine;
      engine.start();
    } catch (error) {
      console.warn('[MyCanteen Promo] Invalid promo configuration:', error);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', boot);
})();
